import React, { useState, useRef } from 'react';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import TextReveal from '../components/TextReveal';
import { AnimatedArrow } from '../components/SVGAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });
    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: "Thank you for reaching out! I'll get back to you soon." });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    backgroundColor: 'transparent',
    color: 'var(--text-primary)',
    borderColor: 'var(--border-strong)',
    fontFamily: 'Nohemi, sans-serif',
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6" data-testid="contact-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-20 text-center">
          <TextReveal
            as="h1"
            className="font-black mb-8"
            style={{ fontSize: 'clamp(4rem, 10vw, 10rem)', lineHeight: '0.92', fontWeight: '900', letterSpacing: '-0.04em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
          >
            Have a Brand Problem?
          </TextReveal>

          <FadeInSection delay={0.3}>
            <div className="flex items-center justify-center gap-4">
              <AnimatedArrow className="opacity-50" />
              <p className="text-3xl" style={{ color: 'var(--text-secondary)', fontFamily: 'Nohemi, sans-serif' }}>
                Let's Solve It.
              </p>
            </div>
          </FadeInSection>
        </div>

        {/* Form */}
        <FadeInSection delay={0.4}>
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold mb-3"
                style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-6 py-4 text-base border-2 focus:outline-none transition-colors"
                style={inputStyle}
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold mb-3"
                style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-6 py-4 text-base border-2 focus:outline-none transition-colors"
                style={inputStyle}
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-semibold mb-3"
                style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={8}
                className="w-full px-6 py-4 text-base border-2 focus:outline-none transition-colors resize-none"
                style={inputStyle}
                data-testid="contact-message-input"
              />
            </div>

            {status.message && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-6 ${status.type === 'success' ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'}`}
                data-testid="contact-status-message"
              >
                <p className="text-base">{status.message}</p>
              </motion.div>
            )}

            <div className="text-center pt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-10 py-4 text-base font-bold uppercase tracking-wide transition-all duration-300 rounded-full hover:opacity-80 hover:scale-105 disabled:opacity-50"
                style={{
                  backgroundColor: 'var(--text-primary)',
                  color: 'var(--bg-primary)',
                  fontFamily: 'Nohemi, sans-serif',
                }}
                data-testid="contact-submit-button"
                data-cursor-hover
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </FadeInSection>
      </div>
    </div>
  );
};

export default Contact;
